"use client";

import clsx from "clsx";

import { buildWistiaIframeSrc } from "@/utils/landingVideo";

/** Placeholder VSL id; swap for the produced "ChatGPT for insurance" video. */
const DEFAULT_MEDIA_ID = "cj25mopmgf";

export type WistiaVslOrientation = "landscape" | "portrait";

type WistiaVslEmbedProps = {
  mediaId?: string;
  autoplay?: boolean;
  /** Accessible name for the video iframe. */
  title?: string;
  orientation?: WistiaVslOrientation;
  /** Omit outer chrome (rounded corners, shadow) when embedding inside phone frames. */
  bare?: boolean;
  className?: string;
};

/** Wistia iframe embed for VSL landing pages. Uses an iframe instead of the
 *  web component so parent-page accessibility audits see captions inside Wistia. */
export function WistiaVslEmbed({
  mediaId = DEFAULT_MEDIA_ID,
  autoplay = false,
  title = "Video",
  orientation = "landscape",
  bare = false,
  className,
}: Readonly<WistiaVslEmbedProps>) {
  const trimmedMediaId = mediaId?.trim() ?? "";
  const resolvedMediaId = trimmedMediaId || DEFAULT_MEDIA_ID;

  return (
    <figure
      className={clsx(
        "relative w-full overflow-hidden",
        !bare && "rounded-[20px] bg-black shadow-[0_24px_60px_-20px_rgba(13,78,176,0.55)]",
        bare && "h-full bg-black",
        className,
      )}
    >
      <div
        className={clsx(
          "relative w-full",
          orientation === "landscape" ? "aspect-video" : "h-full min-h-0",
        )}
      >
        <iframe
          src={buildWistiaIframeSrc(resolvedMediaId, { autoplay })}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture"
          className="absolute inset-0 h-full w-full border-0"
        />
      </div>
    </figure>
  );
}
